export const contentData = {
  en: {
    // Hero Section
    hero: {
      title: "Audiosum | AI-Powered Audio Summary & Video Transcription",
      description: "Audiosum is the leading <strong>audio</strong> and <strong>video</strong> to text converter for professionals, researchers, and students. <strong>Audio transcription made easy</strong> - convert <strong>audio files and video</strong> to text with <b>high quality</b> <strong>technology</strong>. Experience audiosum <b>multiple formats</b> support and <b>read online</b> documentation.",
      badges: [
        "Audio Transcription", 
        "Video Transcription",
        "YouTube",
        "AI Tutor",
        "95+ Languages",
        "PowerPoint",
        "99%+ Accuracy",
        "Image Generator"
      ],
      ctaButton: "Start Free Trial",
      demoButton: "How It Works"
    },
    
    // Benefits Section
    benefits: {
      title: "Audio, Video, & Document Summaries and Analysis",
      cards: [
        {
          icon: "fab fa-youtube",
          title: "YouTube Summary & Analysis",
          description: "Process YouTube videos with our AI audio summarizer and audio transcript generator. Automatically generate chapters, extract key points, and create clear AI summaries for your videos. Instantly download transcripts and summaries for easy reference. Perfect for content creators, educators, researchers, and students who want to convert video to notes quickly and efficiently.",
          gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        },
        {
          icon: "fas fa-file-powerpoint",
          title: "AI Presentation & PowerPoint Generator",
          description: "Automatically transform your content, lectures, meetings, or podcasts into professional PowerPoint presentations with our AI audio summarizer and AI note taker. Generate structured PPTX slides with extracted key points, clear AI summaries, and ready to present decks instantly. Perfect for educators, professionals, and content creators who want to convert audio and video into slides quickly and efficiently.",
          gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        },
        {
          icon: "fas fa-image",
          title: "AI Image Generator & Visual Content Creator",
          description: "Enhance your AI audio summaries, AI lecture notes, and PowerPoint presentations with custom images and visual content generated by advanced AI technology. Create graphics, illustrations, and visual aids that complement your transcriptions, summaries, and slide decks. Perfect for educators, content creators, and professionals who want to turn audio and video content into visually engaging materials quickly and easily.",
          gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        },
        {
          icon: "fas fa-microphone-alt",
          title: "Live Transcription & Real Time Speech to Text",
          description: "Capture meetings, lectures, interviews, and podcasts instantly with real time audio transcription using our AI audio summarizer and audio transcript generator. Convert live speech to text with support for multiple speakers and languages, achieving 99%+ accuracy. Perfect for teams, educators, content creators, and professionals who want to generate AI summaries and notes as conversations happen.",
          gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
        },
        {
          icon: "fas fa-comments",
          title: "AI Chat Assistant & Document Q&A",
          description: "Cross-reference multiple files and saved documents with AI chat. Ask questions about your content and get instant answers from your personal AI tutor. Perfect for research and content analysis with <a href='https://audiosum.app/chatbot' target='_blank' rel='noopener'>intelligent document processing</a>.",
          gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
        },
        {
          icon: "fas fa-exchange-alt",
          title: "Complete Audio & Video Conversion Suite",
          description: "Convert to MP3, MP4, WAV formats. Extract audio from video, reduce noise, normalize volume, compress files. <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>Convert any media format</a> with our complete toolkit for audio and video processing.",
          gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
        }
      ]
    },

    // Features Sections
    features: {
      // Audio Transcription Features
      audioTranscription: {
        title: "Professional Audio Transcription Services & Features",
        description: "Comprehensive audio transcription solutions for professionals, researchers, and students",
        items: [
          {
            icon: "fas fa-microphone",
            title: "High-Quality Audio Transcription",
            description: "Convert <strong>audio recordings</strong> to text with <strong>99%+ accuracy</strong> using advanced audiosum AI technology. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>Upload your audio files</a> to audiosum and start transcribing instantly. The audiosum <b>premium service</b> ensures optimal results for all audiosum users."
          },
          {
            icon: "fas fa-video",
            title: "Video to Text Transcription", 
            description: "<strong>Extract audio</strong> from video files and convert to accurate text transcripts with audiosum. Use our <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>audiosum conversion tool</a> for seamless processing."
          },
          {
            icon: "fas fa-language",
            title: "Multi-language Support",
            description: "<strong>Transcribe audio</strong> in <strong>95+ languages</strong> with native speaker-level accuracy using audiosum. <a href='https://audiosum.app/signup' target='_blank' rel='noopener'>Sign up for audiosum</a> to access all audio language options."
          },
          {
            icon: "fas fa-download",
            title: "Multiple Export Options",
            description: "<strong>Download audio transcripts</strong> in DOCX, PDF, TXT, MD, and other popular formats with audiosum. Manage all your <a href='https://audiosum.app/documents' target='_blank' rel='noopener'>audiosum documents</a> in one place."
          }
        ]
      },

      // Research & Education
      research: {
        title: "Audio Transcription for Research & Education",
        description: "Specialized tools for academic research, interviews, and educational content",
        items: [
          {
            icon: "fas fa-graduation-cap",
            title: "Academic Research Tools",
            description: "Perfect for <strong>qualitative audio research</strong>, interviews, and educational audio content analysis with audiosum. <a href='https://audiosum.ai/en/watch-demo' target='_blank' rel='noopener'>Watch our demo</a> to see how audiosum transforms audio research insights."
          },
          {
            icon: "fas fa-users",
            title: "Interview Transcription",
            description: "<strong>Transcribe audio interviews</strong>, focus groups, and academic audio discussions with audiosum. <a href='https://audiosum.app/login' target='_blank' rel='noopener'>Login to your audiosum account</a> to access previous audio transcriptions."
          },
          {
            icon: "fas fa-chart-line",
            title: "Data Analysis Integration", 
            description: "Export transcripts for use with research analysis software and tools. View your <a href='https://audiosum.app/my-plan' target='_blank' rel='noopener'>subscription plan</a> for export options."
          },
          {
            icon: "fas fa-book",
            title: "Educational Content",
            description: "Convert lectures, seminars, and educational videos to searchable text. Manage settings in your <a href='https://audiosum.app/account' target='_blank' rel='noopener'>account dashboard</a>."
          }
        ]
      },

      // Business Solutions
      business: {
        title: "Business Audio Transcription Solutions",
        description: "Professional transcription services for business meetings, presentations, and corporate content",
        items: [
          {
            icon: "fas fa-briefcase",
            title: "Meeting Transcription",
            description: "Convert business meetings, conferences, and presentations to accurate text. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>Upload recordings</a> for professional transcription."
          },
          {
            icon: "fas fa-handshake",
            title: "Client Call Transcripts",
            description: "Transcribe client calls, sales meetings, and customer interactions. Access your <a href='https://audiosum.app/documents' target='_blank' rel='noopener'>document library</a> anytime."
          },
          {
            icon: "fas fa-cog",
            title: "Workflow Integration",
            description: "Integrate transcription into your existing business workflows and processes. Use our <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>conversion tools</a> for seamless integration."
          },
          {
            icon: "fas fa-shield-alt",
            title: "Enterprise Security",
            description: "Business-grade security and compliance for sensitive corporate content. Review our <a href='https://audiosum.ai/en/terms-of-service' target='_blank' rel='noopener'>terms of service</a> for security and compliance details."
          }
        ]
      }
    }
  },
  
  ar: {
    // Hero Section (Arabic)
    hero: {
      title: "لخص - أداة تلخيص بالذكاء الاصطناعي للملفات الصوتية والفيديو",
      description: "منصة تلخيص المتطورة <strong>لتلخيص النصوص</strong> وتحليل المحتوى <strong>بالذكاء الاصطناعي</strong>. حمل ملفك الصوتي، الفيديو، تسجيل محاضره أو سجل المحاضرة مباشرةً وإحصل على ملخص موجز أو مفصل، نقاط رئيسية مفصلة، أو النص الكامل في بضعة دقائق. حمل أي كتاب، إختبرات، PDF، أو صورة، وإحصل على نتائج سريعة ومفصلة. إنتاج صور بواسطة الذكاء الإصطناعي، وتحويل ملخصاتك إلى بوربوينت جاهزة للعرض مع صور أصلية. إسأل مساعد الذكاء الاصطناعي أي سؤال عن ملفاتك وإحصل على إجابة ومساعدة فورية.",
      badges: [
        "دقة ٩٩٪",
        "٩٥+ لغة", 
        "تلخيص فوري",
        "تلخيص ذكي",
        "تلخيص مجاني",
        "تحويل تلقائي",
        "تلخيص متقدم",
        "تلخيص سريع"
      ],
      ctaButton: "جرب مجاناً!",
      demoButton: "كيف يعمل"
    },
    
    // Benefits Section (Arabic)
    benefits: {
      title: "لماذا يختار المحترفون أداة تلخيص النصوص وتلخيص بالذكاء الاصطناعي المتطور؟",
      cards: [
        {
          icon: "fab fa-youtube",
          title: "معالجة يوتيوب وتحليل الفيديو",
          description: "معالجة فيديوهات يوتيوب مع النسخ بالذكاء الاصطناعي وإنشاء الفصول تلقائياً. البحث في محتوى الفيديو واستخراج النقاط الرئيسية والملخصات وتنزيل النصوص بتنسيقات متعددة. مثالي لمنشئي المحتوى والمعلمين والباحثين. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>ابدأ تحليل الفيديو</a>.",
          gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        },
        {
          icon: "fas fa-file-powerpoint", 
          title: "منشئ باوربوينت بالذكاء الاصطناعي",
          description: "إنشاء عروض باوربوينت احترافية تلقائياً من المحتوى الصوتي والمرئي. إنشاء ملفات PPTX مخصصة مع النقاط الرئيسية والملخصات المستخرجة بالذكاء الاصطناعي. حول المحاضرات والاجتماعات والبودكاست إلى <a href='https://audiosum.app/ar/powerpoint' target='_blank' rel='noopener'>عروض تقديمية جاهزة</a> فوراً.",
          gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        },
        {
          icon: "fas fa-image",
          title: "منشئ الصور بالذكاء الاصطناعي",
          description: "إنشاء صور ومحتوى مرئي مخصص باستخدام تقنية الذكاء الاصطناعي المتقدمة. إنشاء رسومات وتوضيحات ومساعدات بصرية لتكملة النصوص والعروض التقديمية. عزز مستنداتك بصور مُنشأة بالذكاء الاصطناعي مصممة خصيصاً <a href='https://audiosum.app/image-generator' target='_blank' rel='noopener'>لمحتواك</a>.",
          gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        },
        {
          icon: "fas fa-microphone-alt",
          title: "النسخ المباشر وتحويل الكلام إلى نص",
          description: "نسخ صوتي فوري مع تحويل الكلام إلى نص في الوقت الفعلي. التقط الاجتماعات والمحاضرات والمقابلات أثناء حدوثها مع إخراج نصي فوري. دعم لمتحدثين متعددين ولغات متعددة مع <a href='https://audiosum.app/signup' target='_blank' rel='noopener'>دقة عالية</a>.",
          gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
        },
        {
          icon: "fas fa-comments",
          title: "مساعد الدردشة بالذكاء الاصطناعي",
          description: "الإشارة المرجعية لملفات متعددة ومستندات محفوظة مع دردشة الذكاء الاصطناعي. اطرح أسئلة حول محتواك واحصل على إجابات فورية من مدرسك الشخصي بالذكاء الاصطناعي. مثالي للبحث وتحليل المحتوى مع <a href='https://audiosum.app/chatbot' target='_blank' rel='noopener'>معالجة ذكية للمستندات</a>.",
          gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
        },
        {
          icon: "fas fa-exchange-alt",
          title: "مجموعة تحويل كاملة للصوت والفيديو",
          description: "تحويل إلى MP3، MP4، WAV. استخراج الصوت من الفيديو، تقليل الضوضاء، ضبط مستوى الصوت، ضغط الملفات. <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>حول أي تنسيق وسائط</a> مع مجموعة أدواتنا الكاملة لمعالجة الصوت والفيديو.",
          gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
        }
      ]
    },

    // Features Sections (Arabic)
    features: {
      // Audio Transcription Features
      audioTranscription: {
        title: "خدمات وميزات النسخ الصوتي المهنية",
        description: "حلول شاملة للنسخ الصوتي للمحترفين والباحثين والطلاب",
        items: [
          {
            icon: "fas fa-microphone",
            title: "نسخ صوتي عالي الجودة",
            description: "تحويل التسجيلات الصوتية إلى نص بدقة 99%+ باستخدام تقنية الذكاء الاصطناعي المتقدمة. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>ارفع ملفاتك</a> وابدأ النسخ فوراً."
          },
          {
            icon: "fas fa-video",
            title: "نسخ الفيديو إلى نص",
            description: "استخراج الصوت من ملفات الفيديو وتحويله إلى نصوص دقيقة. استخدم <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>أداة التحويل</a> للمعالجة السلسة."
          },
          {
            icon: "fas fa-language",
            title: "دعم متعدد اللغات",
            description: "نسخ الصوت بأكثر من 95 لغة بدقة مستوى المتحدث الأصلي. <a href='https://audiosum.app/signup' target='_blank' rel='noopener'>سجل الآن</a> للوصول لجميع خيارات اللغة."
          },
          {
            icon: "fas fa-download",
            title: "خيارات تصدير متعددة",
            description: "تحميل النصوص بصيغة DOCX وPDF وTXT وMD وصيغ شائعة أخرى. أدر جميع <a href='https://audiosum.app/documents' target='_blank' rel='noopener'>مستنداتك</a> في مكان واحد."
          }
        ]
      },

      // Research & Education
      research: {
        title: "النسخ الصوتي للبحث والتعليم",
        description: "أدوات متخصصة للبحث الأكاديمي والمقابلات والمحتوى التعليمي",
        items: [
          {
            icon: "fas fa-graduation-cap",
            title: "أدوات البحث الأكاديمي",
            description: "مثالي للبحث النوعي والمقابلات وتحليل المحتوى التعليمي. ابدأ مع <a href='https://audiosum.app/chatbot' target='_blank' rel='noopener'>المساعد الذكي</a> للحصول على رؤى بحثية."
          },
          {
            icon: "fas fa-users",
            title: "نسخ المقابلات",
            description: "نسخ مقابلات البحث ومجموعات التركيز والمناقشات الأكاديمية. <a href='https://audiosum.app/login' target='_blank' rel='noopener'>سجل دخولك</a> للوصول للنسخ السابقة."
          },
          {
            icon: "fas fa-chart-line",
            title: "تكامل تحليل البيانات",
            description: "تصدير النصوص للاستخدام مع برامج وأدوات تحليل البحث. اطلع على <a href='https://audiosum.app/my-plan' target='_blank' rel='noopener'>خطة اشتراكك</a> لخيارات التصدير."
          },
          {
            icon: "fas fa-book",
            title: "المحتوى التعليمي",
            description: "تحويل المحاضرات والندوات ومقاطع الفيديو التعليمية إلى نص قابل للبحث. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>ارفع محتواك</a> للبدء."
          }
        ]
      },

      // Business Solutions
      business: {
        title: "حلول النسخ الصوتي للأعمال",
        description: "خدمات نسخ مهنية لاجتماعات الأعمال والعروض التقديمية والمحتوى المؤسسي",
        items: [
          {
            icon: "fas fa-briefcase",
            title: "نسخ الاجتماعات",
            description: "تحويل اجتماعات الأعمال والمؤتمرات والعروض التقديمية إلى نص دقيق. <a href='https://audiosum.app/upload' target='_blank' rel='noopener'>ارفع تسجيلاتك</a> للمعالجة الفورية."
          },
          {
            icon: "fas fa-handshake",
            title: "نصوص مكالمات العملاء",
            description: "نسخ مكالمات العملاء واجتماعات المبيعات وتفاعلات العملاء. <a href='https://audiosum.app/convert' target='_blank' rel='noopener'>استخدم محول الصوت</a> للتحليل الفوري."
          },
          {
            icon: "fas fa-cog",
            title: "تكامل سير العمل",
            description: "دمج النسخ في سير عمل الأعمال والعمليات الحالية. <a href='https://audiosum.app/account' target='_blank' rel='noopener'>أدر إعدادات حسابك</a> للتكامل السلس."
          },
          {
            icon: "fas fa-shield-alt",
            title: "أمان المؤسسات",
            description: "أمان وامتثال على مستوى الأعمال للمحتوى المؤسسي الحساس. <a href='https://audiosum.app/signup' target='_blank' rel='noopener'>سجل للحصول</a> على الميزات المتقدمة."
          }
        ]
      }
    }
  }
};